import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Badge,
  Box,
  Button,
  Container,
  Divider,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  Heading,
  HStack,
  IconButton,
  Image,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Select,
  Switch,
  Tab,
  Table,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tooltip,
  Tr,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";
import { AddIcon, DeleteIcon, EditIcon } from "@chakra-ui/icons";
import api from "./api";

// ─── Types ─────────────────────────────────────────────────────────────────────

interface CommunityQuest {
  _id?: string;
  key: string;
  title: string;
  description: string;
  kind: "COMMUNITY_PHOTO";
  reward: {
    xp: number;
    raffleTickets: number;
    letters: string[];
  };
  target: {
    communityPrompt: string;
    maxSubmissionsPerUser: number;
  };
  enabled: boolean;
}

interface Submission {
  _id: string;
  userId: string;
  address: string;
  questKey: string;
  imageUrl: string;
  caption: string;
  status: "PENDING" | "APPROVED" | "REJECTED";
  adminNote?: string;
  createdAt: string;
}

// ─── Helpers ───────────────────────────────────────────────────────────────────

function slugify(s: string): string {
  return s
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");
}

// ─── Blank quest ───────────────────────────────────────────────────────────────

const BLANK_QUEST: CommunityQuest = {
  key: "",
  title: "",
  description: "",
  kind: "COMMUNITY_PHOTO",
  reward: { xp: 100, raffleTickets: 1, letters: [] },
  target: { communityPrompt: "", maxSubmissionsPerUser: 1 },
  enabled: true,
};

// ─── Shared input style ────────────────────────────────────────────────────────

const fieldStyleProps = {
  bg: "gray.900",
  borderColor: "gray.600",
  color: "white",
  _hover: { borderColor: "gray.500" },
  _focus: {
    borderColor: "brand.500",
    boxShadow: "0 0 0 1px var(--chakra-colors-brand-500)",
  },
};

const inputProps = {
  ...fieldStyleProps,
  size: "sm" as const,
};

// ─── Quest modal (create / edit) ───────────────────────────────────────────────

interface QuestModalProps {
  isOpen: boolean;
  onClose: () => void;
  initial: CommunityQuest | null;
  onSaved: (q: CommunityQuest) => void;
}

function QuestModal({ isOpen, onClose, initial, onSaved }: QuestModalProps) {
  const isEdit = initial !== null;
  const [form, setForm] = useState<CommunityQuest>(initial ?? BLANK_QUEST);
  const [saving, setSaving] = useState(false);
  const [keyManuallyEdited, setKeyManuallyEdited] = useState(false);

  useEffect(() => {
    setForm(initial ?? BLANK_QUEST);
    setKeyManuallyEdited(false);
  }, [initial, isOpen]);

  const set = <K extends keyof CommunityQuest>(k: K, v: CommunityQuest[K]) =>
    setForm((prev) => ({ ...prev, [k]: v }));

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const title = e.target.value;
    set("title", title);
    if (!isEdit && !keyManuallyEdited) {
      set("key", slugify(title));
    }
  };

  const save = async () => {
    if (!form.title.trim()) {
      toast.error("Title is required");
      return;
    }
    if (!form.key.trim()) {
      toast.error("Key is required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        key: form.key,
        title: form.title,
        description: form.description,
        kind: "COMMUNITY_PHOTO" as const,
        reward: {
          xp: form.reward.xp,
          raffleTickets: form.reward.raffleTickets,
          letters: [],
        },
        target: {
          communityPrompt: form.target.communityPrompt,
          maxSubmissionsPerUser: form.target.maxSubmissionsPerUser,
        },
        enabled: form.enabled,
      };
      const res = await api.post("/api/arena/quests", payload);
      const saved: CommunityQuest = res.data.data ?? res.data;
      toast.success(isEdit ? "Quest updated!" : "Quest created!");
      onSaved(saved);
      onClose();
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? "Failed to save quest");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside">
      <ModalOverlay bg="blackAlpha.700" />
      <ModalContent bg="gray.800" borderColor="gray.700" border="1px solid">
        <ModalHeader color="white" borderBottom="1px solid" borderColor="gray.700">
          {isEdit ? "Edit Community Quest" : "New Community Quest"}
        </ModalHeader>
        <ModalCloseButton color="gray.400" />
        <ModalBody py={5}>
          <VStack spacing={4} align="stretch">
            {/* Title */}
            <FormControl>
              <FormLabel color="gray.400" fontSize="sm">
                Title <Text as="span" color="red.400">*</Text>
              </FormLabel>
              <Input
                {...inputProps}
                value={form.title}
                onChange={handleTitleChange}
                placeholder="e.g. Share Your Best Trade"
              />
            </FormControl>

            {/* Key */}
            <FormControl>
              <FormLabel color="gray.400" fontSize="sm">
                Key (slug)
              </FormLabel>
              <Input
                {...inputProps}
                value={form.key}
                isReadOnly={isEdit}
                onChange={(e) => {
                  setKeyManuallyEdited(true);
                  set("key", e.target.value);
                }}
                placeholder="e.g. community-share-trade"
                fontFamily="mono"
                _readOnly={{ opacity: 0.6, cursor: "not-allowed" }}
              />
              {isEdit && (
                <FormHelperText color="gray.500" fontSize="xs">
                  Key cannot be changed after creation.
                </FormHelperText>
              )}
              {!isEdit && (
                <FormHelperText color="gray.500" fontSize="xs">
                  Auto-generated from title. You can edit it.
                </FormHelperText>
              )}
            </FormControl>

            {/* Description */}
            <FormControl>
              <FormLabel color="gray.400" fontSize="sm">
                Description
              </FormLabel>
              <Textarea
                value={form.description}
                onChange={(e) => set("description", e.target.value)}
                placeholder="Quest description shown to users"
                bg="gray.900"
                borderColor="gray.600"
                color="white"
                size="sm"
                rows={2}
                _hover={{ borderColor: "gray.500" }}
                _focus={{
                  borderColor: "brand.500",
                  boxShadow: "0 0 0 1px var(--chakra-colors-brand-500)",
                }}
              />
            </FormControl>

            {/* Prompt */}
            <FormControl>
              <FormLabel color="gray.400" fontSize="sm">
                Prompt
              </FormLabel>
              <Input
                {...inputProps}
                value={form.target.communityPrompt}
                onChange={(e) =>
                  set("target", { ...form.target, communityPrompt: e.target.value })
                }
                placeholder="e.g. Screenshot your most profitable trade this week"
              />
              <FormHelperText color="gray.500" fontSize="xs">
                Instructions shown to the user for their photo submission.
              </FormHelperText>
            </FormControl>

            <Divider borderColor="gray.700" />

            {/* Rewards + Max submissions */}
            <HStack spacing={4} align="flex-start">
              <FormControl>
                <FormLabel color="gray.400" fontSize="sm">
                  XP Reward
                </FormLabel>
                <NumberInput
                  min={0}
                  value={form.reward.xp}
                  onChange={(_, v) =>
                    set("reward", { ...form.reward, xp: isNaN(v) ? 0 : v })
                  }
                  size="sm"
                >
                  <NumberInputField {...fieldStyleProps} />
                  <NumberInputStepper>
                    <NumberIncrementStepper borderColor="gray.600" color="gray.400" />
                    <NumberDecrementStepper borderColor="gray.600" color="gray.400" />
                  </NumberInputStepper>
                </NumberInput>
              </FormControl>
              <FormControl>
                <FormLabel color="gray.400" fontSize="sm">
                  Raffle Tickets
                </FormLabel>
                <NumberInput
                  min={0}
                  value={form.reward.raffleTickets}
                  onChange={(_, v) =>
                    set("reward", { ...form.reward, raffleTickets: isNaN(v) ? 0 : v })
                  }
                  size="sm"
                >
                  <NumberInputField {...fieldStyleProps} />
                  <NumberInputStepper>
                    <NumberIncrementStepper borderColor="gray.600" color="gray.400" />
                    <NumberDecrementStepper borderColor="gray.600" color="gray.400" />
                  </NumberInputStepper>
                </NumberInput>
              </FormControl>
              <FormControl>
                <FormLabel color="gray.400" fontSize="sm">
                  Max submissions / user
                </FormLabel>
                <NumberInput
                  min={1}
                  value={form.target.maxSubmissionsPerUser}
                  onChange={(_, v) =>
                    set("target", {
                      ...form.target,
                      maxSubmissionsPerUser: isNaN(v) ? 1 : v,
                    })
                  }
                  size="sm"
                >
                  <NumberInputField {...fieldStyleProps} />
                  <NumberInputStepper>
                    <NumberIncrementStepper borderColor="gray.600" color="gray.400" />
                    <NumberDecrementStepper borderColor="gray.600" color="gray.400" />
                  </NumberInputStepper>
                </NumberInput>
              </FormControl>
            </HStack>

            <Divider borderColor="gray.700" />

            {/* Enabled */}
            <FormControl display="flex" alignItems="center" gap={3}>
              <Switch
                id="cq-enabled"
                isChecked={form.enabled}
                onChange={(e) => set("enabled", e.target.checked)}
                colorScheme="green"
                size="md"
              />
              <FormLabel htmlFor="cq-enabled" mb={0} color="white" cursor="pointer" fontSize="sm">
                Enabled
              </FormLabel>
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter borderTop="1px solid" borderColor="gray.700" gap={2}>
          <Button variant="ghost" color="gray.400" onClick={onClose} size="sm">
            Cancel
          </Button>
          <Button
            colorScheme="brand"
            onClick={save}
            isLoading={saving}
            loadingText="Saving…"
            size="sm"
          >
            {isEdit ? "Update Quest" : "Create Quest"}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

// ─── Review modal (approve / reject submission) ────────────────────────────────

interface ReviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  submission: Submission | null;
  defaultAction: "approve" | "reject" | null;
  onDone: () => void;
}

function ReviewModal({ isOpen, onClose, submission, defaultAction, onDone }: ReviewModalProps) {
  const [action, setAction] = useState<"approve" | "reject">(defaultAction ?? "approve");
  const [adminNote, setAdminNote] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setAction(defaultAction ?? "approve");
    setAdminNote("");
  }, [submission, isOpen, defaultAction]);

  const submit = async () => {
    if (!submission) return;
    if (action === "reject" && !adminNote.trim()) {
      toast.error("Admin note is required when rejecting");
      return;
    }
    setSaving(true);
    try {
      await api.patch(`/api/arena/community-submissions/${submission._id}/${action}`, {
        adminNote: adminNote.trim() || undefined,
      });
      toast.success(action === "approve" ? "Submission approved!" : "Submission rejected");
      onDone();
      onClose();
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? `Failed to ${action} submission`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" scrollBehavior="inside">
      <ModalOverlay bg="blackAlpha.700" />
      <ModalContent bg="gray.800" borderColor="gray.700" border="1px solid">
        <ModalHeader color="white" borderBottom="1px solid" borderColor="gray.700">
          Review Submission
        </ModalHeader>
        <ModalCloseButton color="gray.400" />
        <ModalBody py={5}>
          {submission && (
            <VStack spacing={4} align="stretch">
              {/* Image preview */}
              <Box
                h="400px"
                bg="gray.900"
                borderRadius="lg"
                overflow="hidden"
                border="1px solid"
                borderColor="gray.700"
              >
                <Image
                  src={submission.imageUrl}
                  alt="Submission"
                  w="100%"
                  h="100%"
                  objectFit="contain"
                  cursor="pointer"
                  onClick={() => window.open(submission.imageUrl, "_blank")}
                />
              </Box>

              {/* Meta */}
              <HStack spacing={4} flexWrap="wrap">
                <Box>
                  <Text color="gray.500" fontSize="xs" textTransform="uppercase" letterSpacing="wider">
                    Address
                  </Text>
                  <Text color="white" fontSize="sm" fontFamily="mono">
                    {submission.address}
                  </Text>
                </Box>
                <Box>
                  <Text color="gray.500" fontSize="xs" textTransform="uppercase" letterSpacing="wider">
                    Quest
                  </Text>
                  <Text color="gray.300" fontSize="sm">
                    {submission.questKey}
                  </Text>
                </Box>
              </HStack>

              {submission.caption && (
                <Box bg="gray.700" borderRadius="md" p={3}>
                  <Text color="gray.400" fontSize="xs" mb={1} textTransform="uppercase" letterSpacing="wider">
                    Caption
                  </Text>
                  <Text color="gray.200" fontSize="sm">
                    {submission.caption}
                  </Text>
                </Box>
              )}

              <Divider borderColor="gray.700" />

              {/* Action selector */}
              <HStack spacing={3}>
                <Button
                  size="sm"
                  colorScheme="green"
                  variant={action === "approve" ? "solid" : "outline"}
                  onClick={() => setAction("approve")}
                >
                  Approve
                </Button>
                <Button
                  size="sm"
                  colorScheme="red"
                  variant={action === "reject" ? "solid" : "outline"}
                  onClick={() => setAction("reject")}
                >
                  Reject
                </Button>
              </HStack>

              {/* Admin note */}
              <FormControl>
                <FormLabel color="gray.400" fontSize="sm">
                  Admin Note{" "}
                  {action === "reject" && (
                    <Text as="span" color="red.400">
                      * (required for reject)
                    </Text>
                  )}
                  {action === "approve" && (
                    <Text as="span" color="gray.500">
                      (optional)
                    </Text>
                  )}
                </FormLabel>
                <Textarea
                  value={adminNote}
                  onChange={(e) => setAdminNote(e.target.value)}
                  placeholder={
                    action === "reject"
                      ? "Reason for rejection…"
                      : "Optional note for the user…"
                  }
                  bg="gray.900"
                  borderColor="gray.600"
                  color="white"
                  size="sm"
                  rows={3}
                  _hover={{ borderColor: "gray.500" }}
                  _focus={{
                    borderColor: "brand.500",
                    boxShadow: "0 0 0 1px var(--chakra-colors-brand-500)",
                  }}
                />
              </FormControl>
            </VStack>
          )}
        </ModalBody>
        <ModalFooter borderTop="1px solid" borderColor="gray.700" gap={2}>
          <Button variant="ghost" color="gray.400" onClick={onClose} size="sm">
            Cancel
          </Button>
          <Button
            colorScheme={action === "approve" ? "green" : "red"}
            onClick={submit}
            isLoading={saving}
            loadingText={action === "approve" ? "Approving…" : "Rejecting…"}
            size="sm"
          >
            {action === "approve" ? "Approve" : "Reject"}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

// ─── Tab 1: Quests ─────────────────────────────────────────────────────────────

function QuestsTab() {
  const [quests, setQuests] = useState<CommunityQuest[]>([]);
  const [loading, setLoading] = useState(true);

  const { isOpen: isModalOpen, onOpen: onModalOpen, onClose: onModalClose } = useDisclosure();
  const [editTarget, setEditTarget] = useState<CommunityQuest | null>(null);

  const [deleteTarget, setDeleteTarget] = useState<CommunityQuest | null>(null);
  const [deleting, setDeleting] = useState(false);
  const cancelRef = useRef<HTMLButtonElement>(null!);

  const loadQuests = () => {
    setLoading(true);
    api
      .get("/api/arena/quests")
      .then((res) => {
        const all: CommunityQuest[] = res.data.data ?? res.data ?? [];
        setQuests(all.filter((q) => q.kind === "COMMUNITY_PHOTO"));
      })
      .catch(() => toast.error("Failed to load community quests"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadQuests();
  }, []);

  const toggleEnabled = async (q: CommunityQuest) => {
    const updated = { ...q, enabled: !q.enabled };
    setQuests((prev) => prev.map((x) => (x.key === q.key ? updated : x)));
    try {
      await api.post("/api/arena/quests", updated);
    } catch (err: any) {
      setQuests((prev) => prev.map((x) => (x.key === q.key ? q : x)));
      toast.error(err?.response?.data?.message ?? "Failed to update quest");
    }
  };

  const openCreate = () => {
    setEditTarget(null);
    onModalOpen();
  };

  const openEdit = (q: CommunityQuest) => {
    setEditTarget(q);
    onModalOpen();
  };

  const onSaved = (saved: CommunityQuest) => {
    setQuests((prev) => {
      const idx = prev.findIndex((q) => q.key === saved.key);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = saved;
        return next;
      }
      return [...prev, saved];
    });
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/api/arena/quests/${deleteTarget.key}`);
      setQuests((prev) => prev.filter((q) => q.key !== deleteTarget.key));
      toast.success("Quest deleted");
      setDeleteTarget(null);
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? "Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box bg="gray.800" borderRadius="xl" border="1px solid" borderColor="gray.700" p={6}>
      <Flex justify="space-between" align="center" mb={4}>
        <Heading size="sm" color="gray.300" textTransform="uppercase" letterSpacing="wider">
          Community Quests ({quests.length})
        </Heading>
        <Button
          colorScheme="brand"
          size="sm"
          leftIcon={<AddIcon boxSize={3} />}
          onClick={openCreate}
        >
          New Quest
        </Button>
      </Flex>

      {loading ? (
        <Flex justify="center" py={10}>
          <Text color="gray.500">Loading quests…</Text>
        </Flex>
      ) : quests.length === 0 ? (
        <Flex
          justify="center"
          align="center"
          py={12}
          direction="column"
          gap={3}
          color="gray.500"
        >
          <Text fontSize="sm">No community quests yet.</Text>
          <Button
            colorScheme="brand"
            size="sm"
            leftIcon={<AddIcon boxSize={3} />}
            onClick={openCreate}
            variant="outline"
          >
            Create your first quest
          </Button>
        </Flex>
      ) : (
        <Box overflowX="auto">
          <Table variant="simple" size="sm">
            <Thead>
              <Tr>
                <Th color="gray.400" borderColor="gray.700">Title</Th>
                <Th color="gray.400" borderColor="gray.700">Prompt</Th>
                <Th color="gray.400" borderColor="gray.700" isNumeric>XP</Th>
                <Th color="gray.400" borderColor="gray.700" isNumeric>Tickets</Th>
                <Th color="gray.400" borderColor="gray.700" isNumeric>Max Sub.</Th>
                <Th color="gray.400" borderColor="gray.700" textAlign="center">Enabled</Th>
                <Th color="gray.400" borderColor="gray.700" />
              </Tr>
            </Thead>
            <Tbody>
              {quests.map((q) => (
                <Tr key={q.key} _hover={{ bg: "gray.750" }}>
                  <Td borderColor="gray.700" py={2.5} maxW="200px">
                    <Text color="white" fontSize="sm" noOfLines={1}>
                      {q.title}
                    </Text>
                    <Text color="gray.500" fontSize="xs" fontFamily="mono">
                      {q.key}
                    </Text>
                  </Td>
                  <Td borderColor="gray.700" py={2.5} maxW="220px">
                    <Text color="gray.300" fontSize="sm" noOfLines={2}>
                      {q.target?.communityPrompt || (
                        <Text as="span" color="gray.600" fontStyle="italic">
                          —
                        </Text>
                      )}
                    </Text>
                  </Td>
                  <Td borderColor="gray.700" py={2.5} isNumeric>
                    <Text color="green.300" fontSize="sm" fontWeight="600">
                      {q.reward?.xp ?? 0}
                    </Text>
                  </Td>
                  <Td borderColor="gray.700" py={2.5} isNumeric>
                    <Text color="yellow.300" fontSize="sm" fontWeight="600">
                      {q.reward?.raffleTickets ?? 0}
                    </Text>
                  </Td>
                  <Td borderColor="gray.700" py={2.5} isNumeric>
                    <Text color="gray.300" fontSize="sm">
                      {q.target?.maxSubmissionsPerUser ?? 1}
                    </Text>
                  </Td>
                  <Td borderColor="gray.700" py={2.5} textAlign="center">
                    <Switch
                      isChecked={q.enabled}
                      onChange={() => toggleEnabled(q)}
                      colorScheme="green"
                      size="sm"
                    />
                  </Td>
                  <Td borderColor="gray.700" py={2.5}>
                    <HStack spacing={1} justify="flex-end">
                      <IconButton
                        aria-label="Edit quest"
                        icon={<EditIcon />}
                        size="xs"
                        colorScheme="blue"
                        variant="ghost"
                        onClick={() => openEdit(q)}
                      />
                      <IconButton
                        aria-label="Delete quest"
                        icon={<DeleteIcon />}
                        size="xs"
                        colorScheme="red"
                        variant="ghost"
                        onClick={() => setDeleteTarget(q)}
                      />
                    </HStack>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}

      <QuestModal
        isOpen={isModalOpen}
        onClose={onModalClose}
        initial={editTarget}
        onSaved={onSaved}
      />

      <AlertDialog
        isOpen={deleteTarget !== null}
        leastDestructiveRef={cancelRef}
        onClose={() => setDeleteTarget(null)}
      >
        <AlertDialogOverlay>
          <AlertDialogContent bg="gray.800" borderColor="gray.700" border="1px solid">
            <AlertDialogHeader color="white">Delete Community Quest</AlertDialogHeader>
            <AlertDialogBody color="gray.300">
              Delete quest{" "}
              <Text as="span" fontWeight="bold" color="white" fontFamily="mono">
                {deleteTarget?.key}
              </Text>
              ? This action cannot be undone.
            </AlertDialogBody>
            <AlertDialogFooter gap={2}>
              <Button
                ref={cancelRef}
                variant="ghost"
                color="gray.400"
                onClick={() => setDeleteTarget(null)}
                size="sm"
              >
                Cancel
              </Button>
              <Button
                colorScheme="red"
                size="sm"
                onClick={confirmDelete}
                isLoading={deleting}
                loadingText="Deleting…"
              >
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
}

// ─── Tab 2: Submissions ────────────────────────────────────────────────────────

function SubmissionsTab() {
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>("ALL");
  const [questKeyFilter, setQuestKeyFilter] = useState<string>("ALL");

  const { isOpen: isReviewOpen, onOpen: onReviewOpen, onClose: onReviewClose } = useDisclosure();
  const [reviewTarget, setReviewTarget] = useState<Submission | null>(null);
  const [defaultAction, setDefaultAction] = useState<"approve" | "reject" | null>(null);

  const loadSubmissions = (status: string, questKey: string) => {
    setLoading(true);
    const params: Record<string, string> = {};
    if (status !== "ALL") params.status = status;
    if (questKey !== "ALL") params.questKey = questKey;
    const qs = new URLSearchParams(params).toString();
    api
      .get(`/api/arena/community-submissions${qs ? `?${qs}` : ""}`)
      .then((res) => {
        const list: Submission[] = res.data.data?.docs ?? res.data.data ?? [];
        setSubmissions(list);
      })
      .catch(() => toast.error("Failed to load submissions"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadSubmissions(statusFilter, questKeyFilter);
  }, [statusFilter, questKeyFilter]);

  const openReview = (sub: Submission, action: "approve" | "reject") => {
    setReviewTarget(sub);
    setDefaultAction(action);
    onReviewOpen();
  };

  // Distinct quest keys from current submissions for filter
  const questKeys = Array.from(new Set(submissions.map((s) => s.questKey))).sort();

  const statusBadge = (status: Submission["status"], adminNote?: string) => {
    const colorMap: Record<string, string> = {
      PENDING: "yellow",
      APPROVED: "green",
      REJECTED: "red",
    };
    const badge = (
      <Badge
        colorScheme={colorMap[status] ?? "gray"}
        fontSize="xs"
        px={2}
        py={0.5}
        borderRadius="md"
        cursor={adminNote ? "help" : "default"}
      >
        {status}
      </Badge>
    );
    if (adminNote && (status === "APPROVED" || status === "REJECTED")) {
      return (
        <Tooltip label={adminNote} placement="top" bg="gray.700" color="white" fontSize="xs" hasArrow>
          {badge}
        </Tooltip>
      );
    }
    return badge;
  };

  return (
    <Box bg="gray.800" borderRadius="xl" border="1px solid" borderColor="gray.700" p={6}>
      {/* Filter bar */}
      <Flex gap={3} mb={4} flexWrap="wrap" align="flex-end">
        <FormControl maxW="160px">
          <FormLabel color="gray.400" fontSize="xs" mb={1}>
            Status
          </FormLabel>
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            bg="gray.900"
            borderColor="gray.600"
            color="white"
            size="sm"
            _hover={{ borderColor: "gray.500" }}
            _focus={{ borderColor: "brand.500" }}
          >
            <option value="ALL" style={{ background: "#1a202c" }}>All</option>
            <option value="PENDING" style={{ background: "#1a202c" }}>Pending</option>
            <option value="APPROVED" style={{ background: "#1a202c" }}>Approved</option>
            <option value="REJECTED" style={{ background: "#1a202c" }}>Rejected</option>
          </Select>
        </FormControl>

        <FormControl maxW="220px">
          <FormLabel color="gray.400" fontSize="xs" mb={1}>
            Quest Key
          </FormLabel>
          <Select
            value={questKeyFilter}
            onChange={(e) => setQuestKeyFilter(e.target.value)}
            bg="gray.900"
            borderColor="gray.600"
            color="white"
            size="sm"
            _hover={{ borderColor: "gray.500" }}
            _focus={{ borderColor: "brand.500" }}
          >
            <option value="ALL" style={{ background: "#1a202c" }}>All Quests</option>
            {questKeys.map((k) => (
              <option key={k} value={k} style={{ background: "#1a202c" }}>
                {k}
              </option>
            ))}
          </Select>
        </FormControl>

        <Button
          size="sm"
          variant="outline"
          colorScheme="gray"
          borderColor="gray.600"
          color="gray.300"
          _hover={{ bg: "gray.700" }}
          onClick={() => loadSubmissions(statusFilter, questKeyFilter)}
        >
          Refresh
        </Button>

        <Text color="gray.500" fontSize="sm" alignSelf="center" ml="auto">
          {submissions.length} result{submissions.length !== 1 ? "s" : ""}
        </Text>
      </Flex>

      {loading ? (
        <Flex justify="center" py={10}>
          <Text color="gray.500">Loading submissions…</Text>
        </Flex>
      ) : submissions.length === 0 ? (
        <Flex justify="center" py={12}>
          <Text color="gray.500" fontSize="sm">
            No submissions found.
          </Text>
        </Flex>
      ) : (
        <Box overflowX="auto">
          <Table variant="simple" size="sm">
            <Thead>
              <Tr>
                <Th color="gray.400" borderColor="gray.700">Thumbnail</Th>
                <Th color="gray.400" borderColor="gray.700">Address</Th>
                <Th color="gray.400" borderColor="gray.700">Quest</Th>
                <Th color="gray.400" borderColor="gray.700">Caption</Th>
                <Th color="gray.400" borderColor="gray.700">Status</Th>
                <Th color="gray.400" borderColor="gray.700">Submitted</Th>
                <Th color="gray.400" borderColor="gray.700">Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {submissions.map((sub) => (
                <Tr key={sub._id} _hover={{ bg: "gray.750" }}>
                  {/* Thumbnail */}
                  <Td borderColor="gray.700" py={2.5}>
                    <Image
                      src={sub.imageUrl}
                      w="60px"
                      h="60px"
                      objectFit="cover"
                      borderRadius="md"
                      cursor="pointer"
                      onClick={() => window.open(sub.imageUrl, "_blank")}
                      fallback={
                        <Box
                          w="60px"
                          h="60px"
                          bg="gray.700"
                          borderRadius="md"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                        >
                          <Text color="gray.500" fontSize="xs">
                            No img
                          </Text>
                        </Box>
                      }
                    />
                  </Td>

                  {/* Address */}
                  <Td borderColor="gray.700" py={2.5} maxW="160px">
                    <Tooltip label={sub.address} placement="top" bg="gray.700" color="white" fontSize="xs" hasArrow>
                      <Text color="gray.300" fontSize="xs" fontFamily="mono" noOfLines={1} cursor="default">
                        {sub.address
                          ? `${sub.address.slice(0, 6)}…${sub.address.slice(-4)}`
                          : "—"}
                      </Text>
                    </Tooltip>
                  </Td>

                  {/* Quest */}
                  <Td borderColor="gray.700" py={2.5} maxW="140px">
                    <Text color="gray.300" fontSize="xs" fontFamily="mono" noOfLines={1}>
                      {sub.questKey}
                    </Text>
                  </Td>

                  {/* Caption */}
                  <Td borderColor="gray.700" py={2.5} maxW="200px">
                    <Text color="gray.300" fontSize="sm" noOfLines={2}>
                      {sub.caption || (
                        <Text as="span" color="gray.600" fontStyle="italic">
                          —
                        </Text>
                      )}
                    </Text>
                  </Td>

                  {/* Status */}
                  <Td borderColor="gray.700" py={2.5}>
                    {statusBadge(sub.status, sub.adminNote)}
                  </Td>

                  {/* Submitted */}
                  <Td borderColor="gray.700" py={2.5} whiteSpace="nowrap">
                    <Text color="gray.400" fontSize="xs">
                      {new Date(sub.createdAt).toLocaleString(undefined, {
                        dateStyle: "short",
                        timeStyle: "short",
                      })}
                    </Text>
                  </Td>

                  {/* Actions */}
                  <Td borderColor="gray.700" py={2.5}>
                    {sub.status === "PENDING" ? (
                      <HStack spacing={2}>
                        <Button
                          size="xs"
                          colorScheme="green"
                          onClick={() => openReview(sub, "approve")}
                        >
                          Approve
                        </Button>
                        <Button
                          size="xs"
                          colorScheme="red"
                          onClick={() => openReview(sub, "reject")}
                        >
                          Reject
                        </Button>
                      </HStack>
                    ) : (
                      <Button
                        size="xs"
                        variant="ghost"
                        colorScheme="gray"
                        color="gray.500"
                        onClick={() => openReview(sub, "approve")}
                      >
                        Review
                      </Button>
                    )}
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}

      <ReviewModal
        isOpen={isReviewOpen}
        onClose={onReviewClose}
        submission={reviewTarget}
        defaultAction={defaultAction}
        onDone={() => loadSubmissions(statusFilter, questKeyFilter)}
      />
    </Box>
  );
}

// ─── Main page ─────────────────────────────────────────────────────────────────

export default function CommunityQuests() {
  return (
    <Container maxW="container.xl" py={8}>
      {/* Header */}
      <Flex justify="space-between" align="center" mb={6}>
        <Box>
          <Heading size="lg" color="white">
            Community Quests
          </Heading>
          <Text color="gray.400" mt={1} fontSize="sm">
            Manage photo-submission quests and review user submissions
          </Text>
        </Box>
      </Flex>

      <Tabs variant="enclosed" colorScheme="brand">
        <TabList borderColor="gray.700" mb={4}>
          <Tab
            color="gray.400"
            _selected={{ color: "white", bg: "gray.800", borderColor: "gray.700", borderBottomColor: "gray.800" }}
            _hover={{ color: "white" }}
          >
            Quests
          </Tab>
          <Tab
            color="gray.400"
            _selected={{ color: "white", bg: "gray.800", borderColor: "gray.700", borderBottomColor: "gray.800" }}
            _hover={{ color: "white" }}
          >
            Submissions
          </Tab>
        </TabList>
        <TabPanels>
          <TabPanel p={0}>
            <QuestsTab />
          </TabPanel>
          <TabPanel p={0}>
            <SubmissionsTab />
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Container>
  );
}
